import { useState } from 'react'

import { makeErrorForKey } from '../../utils'

import type { GivingHomePageBannerContentConfigValue, GivingThemeConfigValue } from '@percent/admin-dashboard/api/types'

export type BrandingEditingKey = 'theme' | 'bannerContent' | null

export const DEFAULT_THEME: GivingThemeConfigValue = {
  colours: {
    primary: '',
    primaryHover: '',
    primaryActive: '',
    primaryFocus: '',
    primary100: '',
    primary400: '',
    primary600: '',
    info30: '',
    info100: '',
    info400: ''
  },
  overlayGradientPercentage: 0
}

const HEX_RE = /^#[0-9a-fA-F]{6}$/

export const isValidHex = (val: string) => HEX_RE.test(val)

type UseBrandingSectionParams = {
  themeData?: GivingThemeConfigValue
  bannerContentData?: GivingHomePageBannerContentConfigValue
  savingKey: string | null
  isSaving: boolean
  mutationError?: string
  resetSaving?: () => void
}

export function useBrandingSection({
  themeData,
  bannerContentData,
  savingKey,
  isSaving,
  mutationError,
  resetSaving
}: UseBrandingSectionParams) {
  const [editingKey, setEditingKey] = useState<BrandingEditingKey>(null)
  const [themeDraft, setThemeDraft] = useState<GivingThemeConfigValue>(DEFAULT_THEME)
  // Raw string for the overlay input so an empty/non-numeric entry can be flagged as invalid
  // rather than silently coerced to 0; themeDraft keeps the parsed number for saving.
  const [overlayInput, setOverlayInput] = useState('')
  const [bannerContentDraft, setBannerContentDraft] = useState<GivingHomePageBannerContentConfigValue>({
    welcomeCopy: false,
    searchBar: false
  })

  const startEditing = (key: BrandingEditingKey) => {
    setEditingKey(key)

    if (key === 'theme') {
      const seed = themeData ?? DEFAULT_THEME

      setThemeDraft(seed)
      setOverlayInput(String(seed.overlayGradientPercentage))
    }

    if (key === 'bannerContent') setBannerContentDraft(bannerContentData ?? { welcomeCopy: false, searchBar: false })
  }

  const handleOverlayChange = (raw: string) => {
    setOverlayInput(raw)

    const parsed = Number(raw)

    if (raw.trim() !== '' && !Number.isNaN(parsed)) {
      setThemeDraft(prev => ({ ...prev, overlayGradientPercentage: parsed }))
    }
  }

  const cancel = () => {
    resetSaving?.()
    setEditingKey(null)
  }

  const errorForKey = makeErrorForKey(savingKey, isSaving, mutationError)

  // The backend theme validator requires all 10 colours to be valid 6-digit hex (zodHexCode, not
  // optional), so an empty/partial theme would 422 — every colour must be a valid hex to save.
  const coloursValid = Object.values(themeDraft.colours).every(val => isValidHex(val))
  const overlayParsed = Number(overlayInput)
  const overlayValid =
    overlayInput.trim() !== '' && !Number.isNaN(overlayParsed) && overlayParsed >= 0 && overlayParsed <= 100
  const themeIsValid = coloursValid && overlayValid

  return {
    editingKey,
    startEditing,
    cancel,
    errorForKey,
    themeDraft,
    setThemeDraft,
    overlayInput,
    handleOverlayChange,
    overlayValid,
    bannerContentDraft,
    setBannerContentDraft,
    themeIsValid
  }
}
